import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import configService from '../services/config';
import {
  Modal, Card, CardContent, Typography, Grid, FormControl, InputLabel,
  Select, MenuItem, TextField, InputAdornment, Box, Button, Dialog,
  DialogContent, DialogActions, IconButton, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, useMediaQuery, useTheme, Stack
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

// ---------- Theme tokens ----------
const ACCENT = '#6c5ce7';
const ACCENT_DARK = '#5b4bc4';
const TEXT_DARK = '#1e293b';
const TEXT_MUTED = '#64748b';
const TEXT_MUTED_2 = '#475569';
const BORDER = '#e2e8f0';
const SURFACE_MUTED = '#f8fafc';

// ---------- Helpers ----------
const fmt = (iso) =>
  !iso ? '—' : new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

const getUTCDateString = (date) => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    .toISOString()
    .split('T')[0];
};

const getLast7DaysUTC = () => {
  const dates = [];
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    dates.push(getUTCDateString(d));
  }
  return dates;
};

const formatDuration = (hours) => {
  if (hours === null || hours === undefined) return '—';
  return `${hours.toFixed(1)} HOURS`;
};

const fmtDate = (iso) =>
  !iso ? '—' : new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const convertTo24Hour = (timeStr) => {
  if (!timeStr || timeStr === '—') return '00:00:00';
  const [time, modifier] = timeStr.split(' ');
  let [hours, minutes] = time.split(':');

  if (hours === '12') { hours = '00'; }
  if (modifier === 'PM') { hours = parseInt(hours, 10) + 12; }
  return `${String(hours).padStart(2, '0')}:${minutes}:00`;
};

const hoursData = [
  { n: '12', deg: 0 }, { n: '01', deg: 30 }, { n: '02', deg: 60 },
  { n: '03', deg: 90 }, { n: '04', deg: 120 }, { n: '05', deg: 150 },
  { n: '06', deg: 180 }, { n: '07', deg: 210 }, { n: '08', deg: 240 },
  { n: '09', deg: 270 }, { n: '10', deg: 300 }, { n: '11', deg: 330 }
];

const minutesData = [
  { n: '00', deg: 0 }, { n: '05', deg: 30 }, { n: '10', deg: 60 },
  { n: '15', deg: 90 }, { n: '20', deg: 120 }, { n: '25', deg: 150 },
  { n: '30', deg: 180 }, { n: '35', deg: 210 }, { n: '40', deg: 240 },
  { n: '45', deg: 270 }, { n: '50', deg: 300 }, { n: '55', deg: 330 }
];

const regChipColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'approved') return 'success';
  if (s === 'rejected') return 'error';
  if (s === 'pending') return 'warning';
  return 'default';
};

export default function AttendanceReport() {
  const theme = useTheme();

  // --- Breakpoints ---
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery('(max-width:420px)');
  const isExtraLarge = useMediaQuery('(min-width:1600px)');
  const isTabletLarge = useMediaQuery('(max-width:992px)');
  const isLandscapeMobile = useMediaQuery('(max-height:500px) and (orientation:landscape)');

  const { company_id, userId } = useSelector((state) => state.globalVariables);

  const [weeklyData, setWeeklyData] = useState([]);
  const [weeklyLoading, setWeeklyLoading] = useState(true);

  const todayDateStr = getUTCDateString(new Date());

  // --- Modal States ---
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState('');
  const [timeType, setTimeType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [beginningTime, setBeginningTime] = useState('12:00 AM');
  const [endTime, setEndTime] = useState('12:00 AM');

  // --- Clock Picker States ---
  const [openPicker, setOpenPicker] = useState(false);
  const [activeField, setActiveField] = useState('beginning');
  const [pickerView, setPickerView] = useState('hours');
  const [selectedHour, setSelectedHour] = useState('12');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const [ampm, setAmpm] = useState('AM');

  // Fetch API Data
  const fetchWeeklySummary = async () => {
    setWeeklyLoading(true);
    try {
      const today = new Date();
      const endDate = getUTCDateString(today);
      const startDateObj = new Date(today);
      startDateObj.setDate(today.getDate() - 6);
      const startDate = getUTCDateString(startDateObj);

      const [attendanceRes, regRes] = await Promise.all([
        configService.getAttendanceByDateRange({ company_id, employee_id: userId, start_date: startDate, end_date: endDate }),
        configService.getRegularizationList({ company_id, employee_id: userId })
      ]);

      let records = [];
      if (attendanceRes?.status || attendanceRes?.data?.status) {
        records = attendanceRes.data?.data || attendanceRes.data || [];
      }
      if (!Array.isArray(records)) records = [];

      let regularizations = [];
      if (regRes?.status || regRes?.data?.status) {
        regularizations = regRes.data?.data || regRes.data || [];
      }
      if (!Array.isArray(regularizations)) regularizations = [];

      const recordMap = {};
      records.forEach((rec) => {
        if (rec?.date) {
          recordMap[getUTCDateString(new Date(rec.date))] = rec;
        }
      });

      const regMap = {};
      regularizations.forEach((reg) => {
        if (reg?.entry_date) {
          regMap[getUTCDateString(new Date(reg.entry_date))] = reg;
        }
      });

      const days = getLast7DaysUTC();
      const summary = days.map((dateKey) => {
        const record = recordMap[dateKey] || null;
        const regularization = regMap[dateKey] || null;
        const [year, month, day] = dateKey.split('-').map(Number);
        const weekday = new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });

        let punchIn = '—', punchOut = '—', durationHours = null;
        const workHour = 8;
        let shortHour = 0, extraHour = 0;
        let isShort = false;

        if (record) {
          punchIn = fmt(record.punch_in);
          punchOut = fmt(record.punch_out);

          if (record.punch_in === 'weekly off' || record.punch_in?.toLowerCase().includes('off')) {
            isShort = false;
          } else if (record.punch_in && record.punch_out) {
            const inTime = new Date(record.punch_in).getTime();
            const outTime = new Date(record.punch_out).getTime();
            if (outTime > inTime) {
              durationHours = (outTime - inTime) / (1000 * 60 * 60);
              durationHours = Math.round(durationHours * 10) / 10;
              if (durationHours > workHour) {
                extraHour = Math.round((durationHours - workHour) * 10) / 10;
              } else if (durationHours < workHour) {
                shortHour = Math.round((workHour - durationHours) * 10) / 10;
                isShort = true;
              }
            } else {
              isShort = true;
              shortHour = workHour;
            }
          } else {
            isShort = true;
            shortHour = workHour;
          }
        } else {
          isShort = true;
          shortHour = workHour;
        }

        let regStatus = '—';
        if (regularization) {
          if (typeof regularization.status === 'string' && regularization.status.trim() !== '') {
            regStatus = regularization.status;
          } else {
            regStatus = 'Pending';
          }
        }

        return {
          rawDate: dateKey,
          date: fmtDate(dateKey),
          week: weekday,
          punchIn,
          punchOut,
          duration: durationHours !== null ? formatDuration(durationHours) : '—',
          workHour: formatDuration(workHour),
          shortHour: shortHour > 0 ? formatDuration(shortHour) : '0 HOURS',
          extraHour: extraHour > 0 ? formatDuration(extraHour) : '0 HOURS',
          isShort: isShort,
          regularization: regStatus,
        };
      });

      setWeeklyData(summary);
    } catch (error) {
      console.error('❌ Fetch Weekly Summary Error:', error);
      setWeeklyData([]);
    } finally {
      setWeeklyLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchWeeklySummary();
  }, [userId, company_id]);

  // --- Modal Handlers ---
  const handleOpenRegularizeModal = (rawDate) => {
    setCategory('');
    setTimeType('');
    setBeginningTime('12:00 AM');
    setEndTime('12:00 AM');
    setSelectedDate(rawDate);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCategory('');
    setTimeType('');
    setBeginningTime('12:00 AM');
    setEndTime('12:00 AM');
    setSelectedDate('');
  };

  const handleSave = async () => {
    if (!category || !timeType || !selectedDate) {
      toast.error('Please fill all the required fields (Category, Time Type, and Date).');
      return;
    }

    const formattedBeginning = convertTo24Hour(beginningTime);
    const formattedEnd = convertTo24Hour(endTime);

    const formData = {
      company_id: company_id,
      employee_id: userId,
      customer_id: userId,
      category: category,
      time_type: timeType,
      entry_date: selectedDate,
      beginning: formattedBeginning,
      end_time: formattedEnd,
      remarks: timeType,
    };

    try {
      setSaving(true);
      const res = await configService.attendanceregularization(formData);

      if (res?.status === true || res?.data?.status === true || res?.status === 201) {
        toast.success('Attendance Regularization saved successfully!');
        handleCloseModal();
        await fetchWeeklySummary();
      } else {
        toast.error(res?.data?.message || res?.message || 'Failed to save.');
      }
    } catch (error) {
      console.error('Error saving attendance regularization:', error);
      toast.error(`Error: ${error?.response?.data?.message || error?.message || 'Server Error'}`);
    } finally {
      setSaving(false);
    }
  };

  // --- Clock Picker Handlers ---
  const handleOpenPicker = (field, currentTime) => {
    setActiveField(field);
    try {
      const [time, period] = currentTime.split(' ');
      const [hr, min] = time.split(':');
      setSelectedHour(hr || '12');
      setSelectedMinute(min || '00');
      setAmpm(period || 'AM');
    } catch (e) {
      setSelectedHour('12');
      setSelectedMinute('00');
      setAmpm('AM');
    }
    setPickerView('hours');
    setOpenPicker(true);
  };

  const handleClockSave = () => {
    const formattedTime = `${selectedHour}:${selectedMinute} ${ampm}`;
    if (activeField === 'beginning') { setBeginningTime(formattedTime); }
    else { setEndTime(formattedTime); }
    setOpenPicker(false);
  };

  const currentData = pickerView === 'hours' ? hoursData : minutesData;
  const currentSelection = pickerView === 'hours' ? selectedHour : selectedMinute;
  const currentRotation = currentData.find(d => d.n === currentSelection)?.deg || 0;

  // --- Responsive values ---
  const rootPadding = isExtraLarge ? 4 : (isSmallMobile ? 1 : (isMobile ? 1.5 : (isTabletLarge ? 2 : 3)));
  const cardPadding = isMobile ? 2 : 3;
  const modalMaxWidth = isSmallMobile ? '100%' : (isMobile ? 400 : 500);
  const clockSize = isLandscapeMobile ? 160 : (isSmallMobile ? 180 : (isMobile ? 190 : 220));
  const clockRadius = isLandscapeMobile ? 55 : (isSmallMobile ? 65 : (isMobile ? 70 : 80));
  const headerVariant = isSmallMobile ? 'subtitle1' : 'h6';
  const buttonFullWidth = isMobile;

  // --- Render Cards for mobile/tablet ---
  const renderCards = () => (
    <Stack spacing={2} sx={{ py: 1, width: '100%' }}>
      {weeklyData.map((row, index) => {
        const isRegSet = row.regularization !== '—';
        const isCurrentDate = row.rawDate === todayDateStr;
        const isOff = row.punchIn === 'weekly off';
        const isShortHour = row.shortHour !== '0 HOURS';
        const status = row.regularization;
        const chipColor = regChipColor(status);

        return (
          <Card key={index} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, boxShadow: 'none', width: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: TEXT_DARK }}>
                    {row.date}
                  </Typography>
                  <Typography variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 500 }}>
                    {row.week}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: TEXT_MUTED_2 }}>
                    <span style={{ fontWeight: 600, color: TEXT_DARK }}>In:</span> {isOff ? 'Weekly Off' : row.punchIn}
                  </Typography>
                  <Typography variant="body2" sx={{ color: TEXT_MUTED_2 }}>
                    <span style={{ fontWeight: 600, color: TEXT_DARK }}>Out:</span> {isOff ? 'Weekly Off' : row.punchOut}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: TEXT_MUTED_2 }}>
                    <span style={{ fontWeight: 600, color: TEXT_DARK }}>Duration:</span> {row.duration}
                  </Typography>
                  <Typography variant="body2" sx={{ color: TEXT_MUTED_2 }}>
                    <span style={{ fontWeight: 600, color: TEXT_DARK }}>Work Hr:</span> {row.workHour}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: TEXT_DARK, fontWeight: isShortHour ? 600 : 400 }}>
                    <span style={{ fontWeight: 600, color: TEXT_DARK }}>Short:</span> {row.shortHour}
                  </Typography>
                  <Typography variant="body2" sx={{ color: TEXT_MUTED_2 }}>
                    <span style={{ fontWeight: 600, color: TEXT_DARK }}>Extra:</span> {row.extraHour}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                  {isRegSet ? (
                    <Chip
                      label={status}
                      size="small"
                      color={chipColor}
                      sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                    />
                  ) : row.isShort ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isCurrentDate}
                      onClick={() => handleOpenRegularizeModal(row.rawDate)}
                      sx={{
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 600,
                        borderColor: isCurrentDate ? 'inherit' : ACCENT,
                        color: isCurrentDate ? 'inherit' : ACCENT,
                        borderRadius: 2,
                        px: 2,
                        py: 0.3,
                        '&:hover': { borderColor: ACCENT_DARK, bgcolor: 'rgba(108, 92, 231, 0.08)' }
                      }}
                    >
                      Apply
                    </Button>
                  ) : (
                    <Typography component="span" sx={{ color: TEXT_MUTED }}>—</Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );

  // --- Render Table (desktop) — short hour now uses TEXT_DARK (black) ---
  const renderTable = () => (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 3,
        border: `1px solid ${BORDER}`,
        overflowX: 'auto',
        maxWidth: '100%',
        width: '100%',
      }}
    >
      <Table
        size="small"
        sx={{
          width: '100%',
          tableLayout: 'auto',
          borderCollapse: 'collapse',
          minWidth: 850,
        }}
      >
        <TableHead>
          <TableRow sx={{ '& th': { bgcolor: SURFACE_MUTED, fontWeight: 700, fontSize: 12, color: TEXT_MUTED_2, letterSpacing: 0.3 } }}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>DATE</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>WEEK</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>PUNCH IN</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>PUNCH OUT</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>DURATION</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>WORK HOUR</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>SHORT HOUR</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>EXTRA HOUR</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>REGULARIZATION</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {weeklyData.map((row, index) => {
            const isRegSet = row.regularization !== '—';
            const isCurrentDate = row.rawDate === todayDateStr;
            const isOff = row.punchIn === 'weekly off';
            const isShortHour = row.shortHour !== '0 HOURS';

            return (
              <TableRow key={index} hover>
                <TableCell sx={{ color: TEXT_DARK, fontSize: 12, whiteSpace: 'nowrap' }}>
                  {row.date}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: TEXT_DARK, fontSize: 12, whiteSpace: 'nowrap' }}>
                  {row.week}
                </TableCell>
                <TableCell sx={{ color: isOff ? TEXT_MUTED : TEXT_DARK, fontStyle: isOff ? 'italic' : 'normal', fontSize: 12, whiteSpace: 'nowrap' }}>
                  {row.punchIn}
                </TableCell>
                <TableCell sx={{ color: row.punchOut === 'weekly off' ? TEXT_MUTED : TEXT_DARK, fontStyle: row.punchOut === 'weekly off' ? 'italic' : 'normal', fontSize: 12, whiteSpace: 'nowrap' }}>
                  {row.punchOut}
                </TableCell>
                <TableCell sx={{ color: TEXT_DARK, fontSize: 12, whiteSpace: 'nowrap' }}>
                  {row.duration}
                </TableCell>
                <TableCell sx={{ color: TEXT_DARK, fontSize: 12, whiteSpace: 'nowrap' }}>
                  {row.workHour}
                </TableCell>
                <TableCell sx={{ color: TEXT_DARK, fontWeight: isShortHour ? 600 : 400, fontSize: 12, whiteSpace: 'nowrap' }}>
                  {row.shortHour}
                </TableCell>
                <TableCell sx={{ color: TEXT_DARK, fontSize: 12, whiteSpace: 'nowrap' }}>
                  {row.extraHour}
                </TableCell>
                <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap', minWidth: 120 }}>
                  {isRegSet ? (
                    <Chip
                      label={row.regularization}
                      size="small"
                      color={regChipColor(row.regularization)}
                      sx={{ fontWeight: 600, textTransform: 'capitalize', fontSize: 11 }}
                    />
                  ) : row.isShort ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isCurrentDate}
                      onClick={() => handleOpenRegularizeModal(row.rawDate)}
                      sx={{
                        textTransform: 'none',
                        fontSize: 11,
                        fontWeight: 600,
                        borderColor: isCurrentDate ? 'inherit' : ACCENT,
                        color: isCurrentDate ? 'inherit' : ACCENT,
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.2,
                        '&:hover': { borderColor: ACCENT_DARK, bgcolor: 'rgba(108, 92, 231, 0.08)' }
                      }}
                    >
                      Apply
                    </Button>
                  ) : (
                    <Typography component="span" sx={{ color: TEXT_MUTED, fontSize: 12 }}>—</Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        p: rootPadding,
        overflowY: 'auto',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        ...(isExtraLarge && { maxWidth: 1400, margin: '0 auto' }),
      }}
    >
      {/* ── Attendance Summary ── */}
      <Card
        sx={{
          borderRadius: 4,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          border: `1px solid ${BORDER}`,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <CardContent sx={{ p: cardPadding, width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
          <Typography variant={headerVariant} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: TEXT_DARK, mb: 2.5 }}>
            <EventAvailableIcon sx={{ color: ACCENT }} />
            Weekly Attendance Summary
          </Typography>

          {weeklyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : weeklyData.length === 0 ? (
            <Typography variant="body2" sx={{ color: TEXT_MUTED, textAlign: 'center', py: 6 }}>
              No attendance data available.
            </Typography>
          ) : (
            <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {isMobileOrTablet ? renderCards() : renderTable()}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Apply Regularization Modal ── */}
      <Modal open={openModal} onClose={handleCloseModal} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, zIndex: 1000 }}>
        <Card sx={{ width: '100%', maxWidth: modalMaxWidth, bgcolor: '#ffffff', borderRadius: 4, color: TEXT_DARK, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: TEXT_DARK }}>
              New Regularization Request
            </Typography>

            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="cat-label">Category</InputLabel>
                  <Select labelId="cat-label" value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
                    <MenuItem value="Working Times">Working Times</MenuItem>
                    <MenuItem value="Break">Break</MenuItem>
                    <MenuItem value="Overtime">Overtime</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="type-label">Time Type</InputLabel>
                  <Select labelId="type-label" value={timeType} label="Time Type" onChange={(e) => setTimeType(e.target.value)}>
                    <MenuItem value="Attendance Regularization">Attendance Regularization</MenuItem>
                    <MenuItem value="On Duty">On Duty</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField label="Date" type="date" value={selectedDate} fullWidth size="small"
                  InputProps={{ readOnly: true, endAdornment: (<InputAdornment position="end"><CalendarTodayIcon sx={{ color: ACCENT, fontSize: '1.2rem' }} /></InputAdornment>) }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField label="Beginning" value={beginningTime} onClick={() => handleOpenPicker('beginning', beginningTime)} fullWidth size="small"
                  InputProps={{ readOnly: true, endAdornment: (<InputAdornment position="end"><AccessTimeIcon sx={{ color: ACCENT, cursor: 'pointer' }} /></InputAdornment>) }}
                  sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField label="End" value={endTime} onClick={() => handleOpenPicker('end', endTime)} fullWidth size="small"
                  InputProps={{ readOnly: true, endAdornment: (<InputAdornment position="end"><AccessTimeIcon sx={{ color: ACCENT, cursor: 'pointer' }} /></InputAdornment>) }}
                  sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField label="Created By" value={userId || 'User'} fullWidth size="small"
                  InputProps={{ readOnly: true, startAdornment: (<InputAdornment position="start"><AccountCircleIcon sx={{ color: '#94a3b8', mr: 0.5 }} /></InputAdornment>) }}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: SURFACE_MUTED } }}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4, flexWrap: 'wrap' }}>
              <Button onClick={handleCloseModal} sx={{ color: TEXT_MUTED, textTransform: 'none' }}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: ACCENT, color: '#fff', px: 3, textTransform: 'none', '&:hover': { bgcolor: ACCENT_DARK }, width: buttonFullWidth ? '100%' : 'auto' }}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Modal>

      {/* ── CUSTOM RADIAL CLOCK DIALOG ── */}
      <Dialog open={openPicker} onClose={() => setOpenPicker(false)} PaperProps={{ sx: { borderRadius: 4, p: isMobile ? 2 : 3, maxWidth: '340px', width: '100%', bgcolor: '#ffffff' } }} sx={{ zIndex: 1100 }}>
        <DialogContent sx={{ p: 0, overflow: 'visible' }}>
          <Typography variant="body2" sx={{ color: TEXT_MUTED, mb: 2, fontWeight: 500 }}>Select time</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            <Box onClick={() => setPickerView('hours')} sx={{ bgcolor: pickerView === 'hours' ? 'rgba(108, 92, 231, 0.1)' : SURFACE_MUTED, px: 2.5, py: 1.5, borderRadius: 2, cursor: 'pointer', border: pickerView === 'hours' ? `1px solid ${ACCENT}` : '1px solid #cbd5e1' }}>
              <Typography variant="h3" sx={{ color: pickerView === 'hours' ? ACCENT : TEXT_DARK, fontWeight: 500 }}>{selectedHour}</Typography>
            </Box>
            <Typography variant="h4" sx={{ color: TEXT_MUTED }}>:</Typography>
            <Box onClick={() => setPickerView('minutes')} sx={{ bgcolor: pickerView === 'minutes' ? 'rgba(108, 92, 231, 0.1)' : SURFACE_MUTED, px: 2.5, py: 1.5, borderRadius: 2, cursor: 'pointer', border: pickerView === 'minutes' ? `1px solid ${ACCENT}` : '1px solid #cbd5e1' }}>
              <Typography variant="h3" sx={{ color: pickerView === 'minutes' ? ACCENT : TEXT_DARK, fontWeight: 500 }}>{selectedMinute}</Typography>
            </Box>
            <ToggleButtonGroup orientation="vertical" value={ampm} exclusive onChange={(e, val) => val && setAmpm(val)} sx={{ ml: 1, bgcolor: SURFACE_MUTED, '& .MuiToggleButton-root': { px: 1.5, py: 0.8 } }}>
              <ToggleButton value="AM">AM</ToggleButton>
              <ToggleButton value="PM">PM</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Box sx={{ width: clockSize, height: clockSize, borderRadius: '50%', bgcolor: SURFACE_MUTED, position: 'relative', border: '1px solid #cbd5e1' }}>
              <Box sx={{ width: 6, height: 6, bgcolor: ACCENT, borderRadius: '50%', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 }} />
              <Box sx={{ position: 'absolute', width: 2, height: clockRadius - 4, bgcolor: ACCENT, bottom: '50%', left: '50%', transformOrigin: 'bottom center', transform: `translateX(-50%) rotate(${currentRotation}deg)`, zIndex: 1 }} />
              {currentData.map((item) => {
                const radius = clockRadius;
                const angleRad = (item.deg - 90) * (Math.PI / 180);
                const x = clockSize/2 + radius * Math.cos(angleRad);
                const y = clockSize/2 + radius * Math.sin(angleRad);
                const isSelected = currentSelection === item.n;

                return (
                  <Box key={item.n} onClick={() => { if (pickerView === 'hours') { setSelectedHour(item.n); setPickerView('minutes'); } else { setSelectedMinute(item.n); } }}
                    sx={{ position: 'absolute', left: x, top: y, transform: 'translate(-50%, -50%)', fontSize: isSmallMobile ? '0.7rem' : '0.85rem', color: isSelected ? '#fff' : TEXT_MUTED_2, cursor: 'pointer', zIndex: 2, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', transition: '0.1s', ...(isSelected && { bgcolor: ACCENT, fontWeight: 'bold' }), }}
                  >
                    {parseInt(item.n)}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 0, pb: 0, pt: 2 }}>
          <IconButton size="small" sx={{ color: TEXT_MUTED }}><KeyboardIcon /></IconButton>
          <Box>
            <Button onClick={() => setOpenPicker(false)} sx={{ textTransform: 'none', fontWeight: 600, color: TEXT_MUTED, mr: 1 }}>Cancel</Button>
            <Button onClick={handleClockSave} sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT }}>OK</Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}